/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */


import React, { useMemo } from 'react';
import { Button } from 'reactstrap';
import { standards } from '@g17eco/core';
import UniversalTracker from '../../../../model/UniversalTracker';
import { QuestionInput } from '../../../question/QuestionInput';
import { CopyTextContainer } from '../../../common/CopyTextContainer';
import { generateUrl } from '../../../../routes/util';
import { ROUTES } from '../../../../constants/routes';
import { UseDataBtn, UseDataBtnProps } from './UseDataBtn';
import { Connection } from '../../../../types/universalTracker';
import { ConnectionUtrv } from '../../../../types/universalTrackerValue';
import './ConnectionQuestion.scss';

interface ConnectionData {
  utrv: ConnectionUtrv;
  utr: UniversalTracker,
}

export interface ConnectionQuestionProps extends Pick<UseDataBtnProps, 'onUseData' | 'className'> {
  utrvs: ConnectionData[]
  connection: Connection;
  selectedStandard: string,
  className?: string;
}

export const ConnectionQuestion = (props: ConnectionQuestionProps) => {

  const {
    selectedStandard,
    connection,
    utrvs,
    className = ''
  } = props;

  const connectionUtrvs = useMemo(() => {
    const utrCodes = Object.values(connection.variables).map(v => v.code)
    return utrvs.filter(v => utrCodes.includes(v.utr.getCode()));
  }, [connection, utrvs])

  const first = connectionUtrvs[0];

  if (!first) {
    return <div className={`related-question ${className}`}>
      No data available
    </div>;
  }

  // Need to figre out what to do with multiple connections utrvs, now select first
  const { utrv, utr } = first;

  const getStandardName = () => {
    const standardName = standards[selectedStandard]?.name || '';
    const standardCode = utr.getTypeCode(selectedStandard);
    if (standardName && standardCode) {
      return `${standardName} - ${standardCode}`;
    }
    return standardName || standardCode || '';
  };

  const hasValue = utrv.value !== undefined;
  const surveyId = utrv.compositeData?.surveyId;

  const onView = (id: string) => {
    const url = `${generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, {
      initiativeId: utrv.initiativeId,
      surveyId: id,
      page: 'question',
    })}/${utrv._id}`;
    window.open(url, '_blank');
  };

  return (
    <div className={`related-question ${className}`}>
      <div className={'standard-text mb-2'}>{getStandardName()}</div>
      <h6 className={'question-label strong mb-3'}>{utr.getValueLabel(selectedStandard)}</h6>
      <QuestionInput utrv={utrv} utr={utr} isReadOnly={true} />

      <CopyTextContainer className={'mb-2'} text={utrv.note} />

      <div className={'button-container'}>
        {surveyId ? <Button color={'link'} onClick={() => onView(surveyId)}>
          <i className={'fa-solid fa-external-link-alt mr-2'} />View
        </Button> : null}
        <UseDataBtn hasValue={hasValue} onUseData={props.onUseData} />
      </div>
    </div>
  )
}
